import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import SectionTasks, { SectionTasksProps } from './SectionTasks';
import TrueImpactText from 'shared/design-system/base/TrueImpactText';
import { Task } from './api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/pro-light-svg-icons';
import { SPACERS } from 'shared/design-system/settings/variables/Spacers';
import { COLORS } from 'shared/design-system/settings/variables/Colors';
import { User } from 'donor/portal/pages/FeedbackDashboard/Feedback/utils';

interface CompletedTasksProps
  extends Omit<SectionTasksProps, 'tasks' | 'showFirstSelected'> {
  isDebug: boolean;
  completedTasks: Task[];
  currentUser: User | undefined;
}

const StyledDiv = styled.div`
  .completed-tasks-button {
    cursor: pointer;
    margin-top: 28px;
    text-align: center;
    color: ${COLORS.ctaPrimary};
    .icon {
      margin-right: ${SPACERS.tiny};
    }
  }
  .load-more-tasks-button {
    cursor: pointer;
    text-align: center;
    margin-top: ${SPACERS.medium};
  }
`;

const SLICE_COUNT = 5;

const CompletedTasks: React.FC<CompletedTasksProps> = ({
  isDebug,
  currentUser,
  completedTasks,
  allReports,
  allPrograms,
  allNPOUsers,
  reloadTasks,
  reloadPrograms,
  reloadNPOUser,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [tasks, setTasks] = useState<Task[]>([]);
  const handleToggle = useCallback(() => {
    setOpen(o => !o);
    setTasks(completedTasks.slice(0, SLICE_COUNT));
  }, [setOpen, setTasks, completedTasks]);

  const handleLoadMore = useCallback(() => {
    setTasks(t => completedTasks.slice(0, t.length + SLICE_COUNT));
  }, [setTasks, completedTasks]);

  if (completedTasks.length === 0) return null;

  return (
    <StyledDiv>
      <TrueImpactText
        variant="body-small"
        onClick={handleToggle}
        className="completed-tasks-button"
      >
        <FontAwesomeIcon icon={open ? faEyeSlash : faEye} className="icon" />
        {open ? 'Hide completed tasks' : 'Show completed tasks'}
      </TrueImpactText>
      {open && (
        <SectionTasks
          isDebug={isDebug}
          tasks={tasks}
          allReports={allReports}
          showFirstSelected={false}
          allPrograms={allPrograms}
          allNPOUsers={allNPOUsers}
          currentUser={currentUser}
          reloadTasks={reloadTasks}
          reloadNPOUser={reloadNPOUser}
          reloadPrograms={reloadPrograms}
        />
      )}
      {open && tasks.length < completedTasks.length && (
        <TrueImpactText
          variant="body-small"
          color="ctaPrimary"
          onClick={handleLoadMore}
          className="load-more-tasks-button"
        >
          Load more completed tasks
        </TrueImpactText>
      )}
    </StyledDiv>
  );
};

export default CompletedTasks;
